import { action, computed } from 'mobx';
import { Entity } from '../../../../core/entity';
import type { IValue } from '@/engine/DataFlow/Graph';
import { Type } from '@/engine/DataFlow/Type';
import type { IIndicationModel } from '../interfaces';
import type * as THREE from 'three';

export interface DistanceProps {
  from?: THREE.Vector3;
  to?: THREE.Vector3;
  distance?: number;
}

export class DistanceModel extends Entity<DistanceProps> implements IIndicationModel, IValue {
  public static readonly Type: Type = new Type('DistanceModel');

  public readonly type: Type = DistanceModel.Type;

  private _updateDistance(): void {
    if (!this.props.from || !this.props.to) {
      this.props.distance = undefined;
    } else {
      this.props.distance = this.props.from.distanceTo(this.props.to);
    }
  }

  public getPointByIndex(index: number): THREE.Vector3 | undefined {
    const keys = ['from', 'to'] as const;
    const key = keys[index];
    return this.props[key];
  }

  public getPoints(): Array<THREE.Vector3 | undefined> {
    return [this.props.from, this.props.to];
  }

  @action
  public setPointByIndex(index: number, point: THREE.Vector3): boolean {
    if (index > 2) return false;
    const keys = ['from', 'to'] as const;
    const key = keys[index];
    this.props[key] = point;
    this._updateDistance();
    return true;
  }

  public get from(): THREE.Vector3 | undefined {
    return this.props.from;
  }

  @action
  public setFromPoint(point: THREE.Vector3 | undefined): void {
    this.props.from = point?.clone();
    this._updateDistance();
  }

  public get to(): THREE.Vector3 | undefined {
    return this.props.to;
  }

  @action
  public setToPoint(point: THREE.Vector3 | undefined): void {
    this.props.to = point?.clone();
    this._updateDistance();
  }

  @computed
  public get distance(): number | undefined {
    return this.props.distance;
  }
}
